import {
  endOfMonth,
  format,
  isEqual,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
  subHours,
  subMonths,
} from 'date-fns'
import dayjs from 'dayjs'
import { useRemoteSelect } from 'hooks/use-remote-select'
import { pluralize } from 'inflection'
import { cn } from 'lib/utils'
import debounce from 'lodash/debounce'
import { Check, Ellipsis, Loader2 } from 'lucide-react'
import { Calendar } from 'primitives/base/calendar'
import { Checkbox } from 'primitives/base/checkbox'
import { Button } from 'primitives/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from 'primitives/command'
import { Popover, PopoverContent, PopoverTrigger } from 'primitives/popover'

import { Tabs, TabsContent, TabsList, TabsTrigger } from 'primitives/tabs'
import { TruncatedTextWithTooltip } from 'primitives/truncated-text-with-tooltip'
import type React from 'react'
import {
  cloneElement,
  isValidElement,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { DateRange } from 'react-day-picker'

// Helper to render icons that can be either ReactElement or ElementType
const renderIcon = (
  IconComponent: React.ReactElement | React.ElementType | undefined,
  className?: string,
  key?: string,
) => {
  if (!IconComponent) return null
  if (isValidElement(IconComponent)) {
    return key ? cloneElement(IconComponent, { key }) : IconComponent
  }
  const Icon = IconComponent as React.ElementType
  return <Icon key={key} className={className} />
}

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'primitives/base/select'
import { Tooltip } from 'primitives/tooltip'
import { numberFilterOperators } from '../core/operators'
import type {
  Column,
  ColumnDataType,
  ColumnOption,
  ColumnOptionExtended,
  DataTableFilterActions,
  FilterModel,
  FilterStrategy,
} from '../core/types'
import { take } from '../lib/array'
import { createNumberRange } from '../lib/helpers'
import { type Locale, t } from '../lib/i18n'
import { DebouncedInput } from '../ui/debounced-input'

interface FilterValueProps<TData, TType extends ColumnDataType> {
  filter: FilterModel<TType>
  column: Column<TData, TType>
  actions: DataTableFilterActions
  strategy: FilterStrategy
  locale?: Locale
}

export const FilterValue = memo(__FilterValue) as typeof __FilterValue

function __FilterValue<TData, TType extends ColumnDataType>({
  filter,
  column,
  actions,
  strategy,
  locale,
}: FilterValueProps<TData, TType>) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="m-0 h-full w-fit whitespace-nowrap rounded-none p-0 px-2 font-inter text-s-400 normal-case tracking-normal"
        >
          <FilterValueDisplay filter={filter} column={column} actions={actions} locale={locale} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className="w-fit max-w-96 p-0 origin-(--radix-popover-content-transform-origin)"
      >
        <FilterValueController
          filter={filter}
          column={column}
          actions={actions}
          strategy={strategy}
          locale={locale}
        />
      </PopoverContent>
    </Popover>
  )
}

interface FilterValueDisplayProps<TData, TType extends ColumnDataType> {
  filter: FilterModel<TType>
  column: Column<TData, TType>
  actions: DataTableFilterActions
  locale?: Locale
}

export function FilterValueDisplay<TData, TType extends ColumnDataType>({
  filter,
  column,
  actions,
  locale = 'en',
}: FilterValueDisplayProps<TData, TType>) {
  switch (column.type) {
    case 'option':
      return (
        <FilterValueOptionDisplay
          filter={filter as FilterModel<'option'>}
          column={column as Column<TData, 'option'>}
          actions={actions}
          locale={locale}
        />
      )
    case 'multiOption':
      return (
        <FilterValueMultiOptionDisplay
          filter={filter as FilterModel<'multiOption'>}
          column={column as Column<TData, 'multiOption'>}
          actions={actions}
          locale={locale}
        />
      )
    case 'date':
      return (
        <FilterValueDateDisplay
          filter={filter as FilterModel<'date'>}
          column={column as Column<TData, 'date'>}
          actions={actions}
          locale={locale}
        />
      )
    case 'text':
      return (
        <FilterValueTextDisplay
          filter={filter as FilterModel<'text'>}
          column={column as Column<TData, 'text'>}
          actions={actions}
          locale={locale}
        />
      )
    case 'number':
      return (
        <FilterValueNumberDisplay
          filter={filter as FilterModel<'number'>}
          column={column as Column<TData, 'number'>}
          actions={actions}
          locale={locale}
        />
      )
    case 'boolean':
      return (
        <FilterValueBooleanDisplay
          filter={filter as FilterModel<'boolean'>}
          column={column as Column<TData, 'boolean'>}
          actions={actions}
          locale={locale}
        />
      )
    case 'durationRange':
      return (
        <FilterValueDurationRangeDisplay
          filter={filter as FilterModel<'durationRange'>}
          column={column as Column<TData, 'durationRange'>}
          actions={actions}
          locale={locale}
        />
      )
    default:
      return null
  }
}

export function FilterValueOptionDisplay<TData>({
  filter,
  column,
  actions: _actions,
  locale: _locale = 'en',
}: FilterValueDisplayProps<TData, 'option'>) {
  // biome-ignore lint/suspicious/noExplicitAny: Remote config needs flexible typing
  const remoteConfig = (column as any).remoteOptionsConfig

  const options = useMemo(() => {
    try {
      return column.getOptions()
    } catch {
      return []
    }
  }, [column])
  const selected = options.filter((o) => filter?.values.includes(o.value))

  // For remote option columns, if selected value not found in static options, try to fetch it
  if (selected.length === 0 && filter?.values.length > 0 && remoteConfig) {
    // Use remote display to resolve label from remote options
    if (remoteConfig.fetchService) {
      return (
        <FilterValueRemoteOptionDisplay<TData>
          filter={filter}
          column={column as Column<TData, 'option'>}
          remoteConfig={remoteConfig}
        />
      )
    }

    // Otherwise, show raw value
    return <span>{filter.values[0]}</span>
  }

  if (selected.length === 1) {
    const { label, labelString, icon } = selected[0]
    const hasIcon = !!icon
    const displayText = labelString ?? (typeof label === 'string' ? label : undefined)
    return (
      <span className="inline-flex items-center gap-1 min-w-0">
        {hasIcon && renderIcon(icon, 'size-4 text-primary shrink-0')}
        <span className="truncate" title={displayText}>
          {labelString ?? label}
        </span>
      </span>
    )
  }
  const name = column.displayName.toLowerCase()
  const pluralName = column.pluralLabel ?? pluralize(name)

  const hasOptionIcons = !options?.some((o) => !o.icon)

  return (
    <div className="inline-flex items-center gap-0.5">
      {hasOptionIcons &&
        take(selected, 3).map(({ value, icon }) => renderIcon(icon, 'size-4', value))}
      <span className={cn(hasOptionIcons && 'ml-1.5')}>
        {selected.length} {pluralName}
      </span>
    </div>
  )
}

function FilterValueRemoteOptionDisplay<TData>({
  filter,
  column,
  remoteConfig,
}: Omit<FilterValueDisplayProps<TData, 'option'>, 'actions' | 'locale'> & {
  remoteConfig: NonNullable<unknown>
}) {
  // biome-ignore lint/suspicious/noExplicitAny: Remote config needs flexible typing
  const config = remoteConfig as any
  const selectedValue = filter?.values?.[0]

  const hasFetchResource = Boolean(config.fetchResource && config.optionFromResource)
  const { options: remoteOptions, isLoading } = useRemoteSelect({
    queryKey: config.queryKey,
    fetchService: config.fetchService,
    optionExtractor: config.optionExtractor,
    searchKey: config.searchKey,
    pageSize: config.pageSize,
    enableOffsetMode: config.enableOffsetMode ?? false,
    selectedValue: hasFetchResource ? selectedValue : undefined,
    fetchResource: config.fetchResource,
    optionFromResource: config.optionFromResource,
    enabled: true,
  })

  // Merge static options with remote options
  const staticOptions = useMemo(() => {
    try {
      return column.getOptions()
    } catch {
      return []
    }
  }, [column])

  const allOptions = useMemo(() => {
    const remoteValues = new Set(remoteOptions.map((o) => o.value))
    const uniqueStatic = staticOptions.filter((o) => !remoteValues.has(o.value))
    return [...uniqueStatic, ...remoteOptions]
  }, [staticOptions, remoteOptions]) as Array<{
    label: string | React.ReactNode
    labelString?: string
    value: string
    icon?: React.ReactElement | React.ElementType
  }>

  const selected = allOptions.filter((o) => filter?.values.includes(o.value))

  if (isLoading) {
    return <Ellipsis className="size-4" />
  }

  if (selected.length === 0 && filter?.values.length > 0) {
    return <span>{filter.values[0]}</span>
  }

  if (selected.length === 1) {
    const { label, labelString, icon } = selected[0]
    const hasIcon = !!icon && (isValidElement(icon) || typeof icon === 'function')
    const displayText = labelString ?? (typeof label === 'string' ? label : undefined)
    return (
      <span className="inline-flex items-center gap-1 min-w-0">
        {hasIcon &&
          renderIcon(
            icon as React.ReactElement | React.ElementType,
            'size-4 text-primary shrink-0',
          )}
        <Tooltip content={displayText ?? undefined}>
          <span className="truncate">{labelString ?? label ?? ''}</span>
        </Tooltip>
      </span>
    )
  }

  const name = column.displayName.toLowerCase()
  const pluralName = column.pluralLabel ?? pluralize(name)
  const hasOptionIcons = !allOptions?.some((o) => !o.icon)

  return (
    <div className="inline-flex items-center gap-0.5">
      {hasOptionIcons &&
        take(selected, 3)
          .filter(({ icon }) => icon && (isValidElement(icon) || typeof icon === 'function'))
          .map(({ value, icon }) =>
            renderIcon(icon as React.ReactElement | React.ElementType, 'size-4', value),
          )}
      <span className={cn(hasOptionIcons && 'ml-1.5')}>
        {selected.length} {pluralName}
      </span>
    </div>
  )
}

export function FilterValueMultiOptionDisplay<TData>({
  filter,
  column,
  actions: _actions,
  locale: _locale = 'en',
}: FilterValueDisplayProps<TData, 'multiOption'>) {
  const options = useMemo(() => {
    try {
      return column.getOptions()
    } catch {
      return []
    }
  }, [column])
  const selected = options.filter((o) => filter.values.includes(o.value))

  // For remote option columns, if selected values not found in static options, show raw values
  if (selected.length === 0 && filter.values.length > 0) {
    if (filter.values.length === 1) return <span>{filter.values[0]}</span>
    const name = column.displayName.toLowerCase()
    const pluralName = column.pluralLabel ?? pluralize(name)
    return (
      <span>
        {filter.values.length} {pluralName}
      </span>
    )
  }

  if (selected.length === 1) {
    const { label, labelString, icon } = selected[0]
    const hasIcon = !!icon
    return (
      <span className="inline-flex items-center gap-1.5">
        {hasIcon && renderIcon(icon, 'size-4 text-primary')}
        <span>{labelString ?? label}</span>
      </span>
    )
  }

  const name = column.displayName.toLowerCase()
  const pluralName = column.pluralLabel ?? pluralize(name)

  const hasOptionIcons = !options?.some((o) => !o.icon)

  return (
    <div className="inline-flex items-center gap-1.5">
      {hasOptionIcons && (
        <div key="icons" className="inline-flex items-center gap-0.5">
          {take(selected, 3).map(({ value, icon }) => renderIcon(icon, 'size-4', value))}
        </div>
      )}
      <span>
        {selected.length} {pluralName}
      </span>
    </div>
  )
}

// Parse date value as local midnight to avoid off-by-one display errors.
// new Date('YYYY-MM-DD') creates UTC midnight per JS spec, which shifts the date
// backward when displayed in negative UTC offset timezones. dayjs treats
// date-only strings as local time, avoiding this issue.
const toLocalDate = (value: Date | string): Date =>
  value instanceof Date ? value : dayjs(value).toDate()

function formatDateRange(start: Date | string, end: Date | string) {
  const startDate = toLocalDate(start)
  const endDate = toLocalDate(end)

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return 'Invalid date range'
  }

  const sameMonth = startDate.getMonth() === endDate.getMonth()
  const sameYear = startDate.getFullYear() === endDate.getFullYear()

  if (sameMonth && sameYear) {
    return `${format(startDate, 'MMM d')} - ${format(endDate, 'd, yyyy')}`
  }

  if (sameYear) {
    return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`
  }

  return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`
}

export function FilterValueDateDisplay<TData>({
  filter,
  column: _column,
  actions: _actions,
  locale: _locale = 'en',
}: FilterValueDisplayProps<TData, 'date'>) {
  if (!filter) return null
  if (filter.values.length === 0) return <Ellipsis className="size-4" />
  if (filter.values.length === 1) {
    const value = filter.values[0]
    const dateValue = toLocalDate(value)

    if (Number.isNaN(dateValue.getTime())) {
      return <span>Invalid date</span>
    }

    const formattedDateStr = format(dateValue, 'MMM d, yyyy')

    return (
      <span className="font-jetbrains text-xs-400 tabular-nums normal-case tracking-normal">
        {formattedDateStr}
      </span>
    )
  }

  const formattedRangeStr = formatDateRange(filter.values[0], filter.values[1])

  return (
    <span className="font-jetbrains text-xs-400 tabular-nums normal-case tracking-normal">
      {formattedRangeStr}
    </span>
  )
}

export function FilterValueTextDisplay<TData>({
  filter,
  column: _column,
  actions: _actions,
  locale: _locale = 'en',
}: FilterValueDisplayProps<TData, 'text'>) {
  if (!filter) return null
  if (filter.values.length === 0 || filter.values[0].trim() === '')
    return <Ellipsis className="size-4" />

  const value = filter.values[0]

  return <TruncatedTextWithTooltip>{value}</TruncatedTextWithTooltip>
}

export function FilterValueNumberDisplay<TData>({
  filter,
  column: _column,
  actions: _actions,
  locale = 'en',
}: FilterValueDisplayProps<TData, 'number'>) {
  if (!filter || !filter.values || filter.values.length === 0) return null

  if (filter.operator === 'is between' || filter.operator === 'is not between') {
    const minValue = filter.values[0]
    const maxValue = filter.values[1]

    return (
      <span className="font-jetbrains text-xs-400 tabular-nums normal-case tracking-normal">
        {minValue} {t('and', locale)} {maxValue}
      </span>
    )
  }

  const value = filter.values[0]
  return (
    <span className="font-jetbrains text-xs-400 tabular-nums normal-case tracking-normal">
      {value}
    </span>
  )
}

export function FilterValueBooleanDisplay<TData>({
  filter,
}: FilterValueDisplayProps<TData, 'boolean'>) {
  if (!filter || filter.values.length === 0) return null
  const value = filter.values[0]
  return <span>{value ? 'Yes' : 'No'}</span>
}

export function FilterValueDurationRangeDisplay<TData>({
  filter,
  column,
}: FilterValueDisplayProps<TData, 'durationRange'>) {
  if (!filter || !filter.values || filter.values.length < 2) return null

  const fromValue = String(filter.values[0])
  const toValue = String(filter.values[1])

  const fromOptions = column.durationRangeOptions?.fromOptions ?? []
  const toOptions = column.durationRangeOptions?.toOptions ?? []

  const fromLabel = fromOptions.find((o) => o.value === fromValue)?.label ?? fromValue
  const toLabel = toOptions.find((o) => o.value === toValue)?.label ?? toValue

  return (
    <span className="font-jetbrains text-xs-400 tabular-nums normal-case tracking-normal">
      {fromLabel} - {toLabel}
    </span>
  )
}

/****** Property Filter Value Controller ******/

interface FilterValueControllerProps<TData, TType extends ColumnDataType> {
  filter: FilterModel<TType> | undefined
  column: Column<TData, TType>
  actions: DataTableFilterActions
  strategy: FilterStrategy
  locale?: Locale
  onClose?: () => void
}

export const FilterValueController = memo(__FilterValueController) as typeof __FilterValueController

function __FilterValueController<TData, TType extends ColumnDataType>({
  filter,
  column,
  actions,
  strategy,
  locale = 'en',
  onClose,
}: FilterValueControllerProps<TData, TType>) {
  switch (column.type) {
    case 'option':
      return (
        <FilterValueOptionController
          filter={filter as FilterModel<'option'>}
          column={column as Column<TData, 'option'>}
          actions={actions}
          strategy={strategy}
          locale={locale}
        />
      )
    case 'multiOption':
      return (
        <FilterValueMultiOptionController
          filter={filter as FilterModel<'multiOption'>}
          column={column as Column<TData, 'multiOption'>}
          actions={actions}
          strategy={strategy}
          locale={locale}
        />
      )
    case 'date':
      return (
        <FilterValueDateController
          filter={filter as FilterModel<'date'>}
          column={column as Column<TData, 'date'>}
          actions={actions}
          strategy={strategy}
          locale={locale}
          onClose={onClose}
        />
      )
    case 'text':
      return (
        <FilterValueTextController
          filter={filter as FilterModel<'text'>}
          column={column as Column<TData, 'text'>}
          actions={actions}
          strategy={strategy}
          locale={locale}
        />
      )
    case 'number':
      return (
        <FilterValueNumberController
          filter={filter as FilterModel<'number'>}
          column={column as Column<TData, 'number'>}
          actions={actions}
          strategy={strategy}
          locale={locale}
        />
      )
    case 'boolean':
      return null
    case 'durationRange':
      return (
        <FilterValueDurationRangeController
          filter={filter as FilterModel<'durationRange'>}
          column={column as Column<TData, 'durationRange'>}
          actions={actions}
          strategy={strategy}
          locale={locale}
        />
      )
    default:
      return null
  }
}

interface OptionItemProps {
  option: ColumnOptionExtended & { initialSelected: boolean }
  onToggle: (value: string, checked: boolean) => void
}

// Memoized option item to prevent re-renders unless its own props change
const OptionItem = memo(function OptionItem({ option, onToggle }: OptionItemProps) {
  const { value, label, icon: Icon, selected } = option
  const handleSelect = useCallback(() => {
    onToggle(value, !selected)
  }, [onToggle, value, selected])

  return (
    <CommandItem
      key={value}
      onSelect={handleSelect}
      className="group flex items-center justify-between gap-1.5 overflow-hidden"
    >
      <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
        <Checkbox
          checked={selected}
          className="opacity-0 data-[state=checked]:opacity-100 group-data-[selected=true]:opacity-100 dark:border-ring mr-1 shrink-0"
        />
        {Icon && renderIcon(Icon, 'size-4 text-primary shrink-0')}
        <span className="truncate" title={typeof label === 'string' ? label : undefined}>
          {label}
        </span>
      </div>
    </CommandItem>
  )
})

export function FilterValueOptionController<TData>(
  props: FilterValueControllerProps<TData, 'option'>,
) {
  // biome-ignore lint/suspicious/noExplicitAny: Remote config needs flexible typing
  const remoteConfig = (props.column as any).remoteOptionsConfig
  if (remoteConfig) {
    return <FilterValueRemoteOptionController {...props} remoteConfig={remoteConfig} />
  }
  return <FilterValueStaticOptionController {...props} />
}

function FilterValueStaticOptionController<TData>({
  filter,
  column,
  actions,
  locale = 'en',
}: FilterValueControllerProps<TData, 'option'>) {
  // Compute initial options once per mount
  const initialOptions = useMemo(() => {
    const counts = column.getFacetedUniqueValues()
    return column.getOptions().map((o) => {
      const isSelected = filter?.values.includes(o.value) ?? false
      return {
        ...o,
        selected: isSelected,
        initialSelected: isSelected,
        count: counts?.get(o.value) ?? 0,
      }
    })
  }, [filter, column])

  const [options, setOptions] = useState(initialOptions)

  // Update selected state when filter values change
  useEffect(() => {
    setOptions((prev) =>
      prev.map((o) => ({ ...o, selected: filter?.values.includes(o.value) ?? false })),
    )
  }, [filter?.values])

  const handleToggle = useCallback(
    (value: string, checked: boolean) => {
      if (checked) actions.addFilterValue(column, [value])
      else actions.removeFilterValue(column, [value])
    },
    [actions, column],
  )

  // Derive groups based on `initialSelected` only
  const { selectedOptions, unselectedOptions } = useMemo(() => {
    const sel: typeof options = []
    const unsel: typeof options = []
    for (const o of options) {
      if (o.initialSelected) sel.push(o)
      else unsel.push(o)
    }
    return { selectedOptions: sel, unselectedOptions: unsel }
  }, [options])

  return (
    <Command loop>
      <CommandInput autoFocus placeholder={t('search', locale)} />
      <CommandEmpty>{t('noresults', locale)}</CommandEmpty>
      <CommandList>
        <CommandGroup className={cn(selectedOptions.length === 0 && 'hidden')}>
          {selectedOptions.map((option) => (
            <OptionItem key={option.value} option={option} onToggle={handleToggle} />
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup className={cn(unselectedOptions.length === 0 && 'hidden')}>
          {unselectedOptions.map((option) => (
            <OptionItem key={option.value} option={option} onToggle={handleToggle} />
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

function FilterValueRemoteOptionController<TData>({
  filter,
  column,
  actions,
  remoteConfig,
  locale = 'en',
}: FilterValueControllerProps<TData, 'option'> & { remoteConfig: NonNullable<unknown> }) {
  // biome-ignore lint/suspicious/noExplicitAny: Remote config needs flexible typing
  const config = remoteConfig as any

  const {
    options: remoteOptions,
    setSearchTerm,
    isLoading,
    loadMore,
    hasNextPage,
    isFetchingNextPage,
  } = useRemoteSelect({
    queryKey: config.queryKey,
    fetchService: config.fetchService,
    optionExtractor: config.optionExtractor,
    searchKey: config.searchKey,
    pageSize: config.pageSize,
    enableOffsetMode: config.enableOffsetMode ?? false,
    selectedValue:
      config.fetchResource && config.optionFromResource
        ? filter?.values?.[0] || config.selectedValue
        : undefined,
    fetchResource: config.fetchResource,
    optionFromResource: config.optionFromResource,
  })

  // Merge static options (e.g. "Main Account") from column with remote options
  const staticOptions = useMemo(() => {
    try {
      return column.getOptions()
    } catch {
      return []
    }
  }, [column])

  const allOptions: ColumnOption[] = useMemo(() => {
    const remoteValues = new Set(remoteOptions.map((o) => o.value))
    // Static options that aren't duplicated in remote results go first
    const uniqueStatic = staticOptions.filter((o) => !remoteValues.has(o.value))
    const normalizedRemote: ColumnOption[] = remoteOptions.map((o) => ({
      label: o.label,
      value: o.value,
      icon: o.icon as ColumnOption['icon'],
    }))
    return [...uniqueStatic, ...normalizedRemote]
  }, [staticOptions, remoteOptions])

  const options: Array<ColumnOptionExtended & { initialSelected: boolean }> = useMemo(
    () =>
      allOptions.map((o) => ({
        ...o,
        selected: filter?.values.includes(o.value) ?? false,
        initialSelected: filter?.values.includes(o.value) ?? false,
        count: 0,
      })),
    [allOptions, filter?.values],
  )

  const handleToggle = useCallback(
    (value: string, checked: boolean) => {
      if (checked) actions.addFilterValue(column, [value])
      else actions.removeFilterValue(column, [value])
    },
    [actions, column],
  )

  const { selectedOptions, unselectedOptions } = useMemo(() => {
    const sel: typeof options = []
    const unsel: typeof options = []
    for (const o of options) {
      if (o.selected) sel.push(o)
      else unsel.push(o)
    }
    return { selectedOptions: sel, unselectedOptions: unsel }
  }, [options])

  const listRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    const el = listRef.current
    if (!el || !hasNextPage || isFetchingNextPage) return
    const { scrollTop, scrollHeight, clientHeight } = el
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      loadMore()
    }
  }, [hasNextPage, isFetchingNextPage, loadMore])

  const useClientSideSearch = config.clientSideSearch ?? false

  return (
    <Command loop shouldFilter={useClientSideSearch}>
      <CommandInput
        autoFocus
        placeholder={t('search', locale)}
        onValueChange={useClientSideSearch ? undefined : setSearchTerm}
      />
      {!isLoading && allOptions.length === 0 && (
        <CommandEmpty>{t('noresults', locale)}</CommandEmpty>
      )}
      <CommandList ref={listRef} onScroll={handleScroll}>
        {isLoading && allOptions.length === 0 ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <CommandGroup className={cn(selectedOptions.length === 0 && 'hidden')}>
              {selectedOptions.map((option) => (
                <OptionItem key={option.value} option={option} onToggle={handleToggle} />
              ))}
            </CommandGroup>
            {selectedOptions.length > 0 && unselectedOptions.length > 0 && <CommandSeparator />}
            <CommandGroup className={cn(unselectedOptions.length === 0 && 'hidden')}>
              {unselectedOptions.map((option) => (
                <OptionItem key={option.value} option={option} onToggle={handleToggle} />
              ))}
            </CommandGroup>
            {isFetchingNextPage && (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="size-3 animate-spin text-muted-foreground" />
              </div>
            )}
          </>
        )}
      </CommandList>
    </Command>
  )
}

export function FilterValueMultiOptionController<TData>(
  props: FilterValueControllerProps<TData, 'multiOption'>,
) {
  // biome-ignore lint/suspicious/noExplicitAny: Remote config needs flexible typing
  const remoteConfig = (props.column as any).remoteOptionsConfig
  if (remoteConfig) {
    return <FilterValueRemoteMultiOptionController {...props} remoteConfig={remoteConfig} />
  }
  return <FilterValueStaticMultiOptionController {...props} />
}

function FilterValueStaticMultiOptionController<TData>({
  filter,
  column,
  actions,
  locale = 'en',
}: FilterValueControllerProps<TData, 'multiOption'>) {
  // Compute initial options once per mount
  const initialOptions = useMemo(() => {
    const counts = column.getFacetedUniqueValues()
    return column.getOptions().map((o) => {
      const selected = filter?.values.includes(o.value) ?? false
      return {
        ...o,
        selected,
        initialSelected: selected,
        count: counts?.get(o.value) ?? 0,
      }
    })
  }, [filter, column])

  const [options, setOptions] = useState(initialOptions)

  // Update selected state when filter values change
  useEffect(() => {
    setOptions((prev) =>
      prev.map((o) => ({ ...o, selected: filter?.values.includes(o.value) ?? false })),
    )
  }, [filter?.values])

  const handleToggle = useCallback(
    (value: string, checked: boolean) => {
      if (checked) actions.addFilterValue(column, [value])
      else actions.removeFilterValue(column, [value])
    },
    [actions, column],
  )

  // Derive groups based on `initialSelected` only
  const { selectedOptions, unselectedOptions } = useMemo(() => {
    const sel: typeof options = []
    const unsel: typeof options = []
    for (const o of options) {
      if (o.initialSelected) sel.push(o)
      else unsel.push(o)
    }
    return { selectedOptions: sel, unselectedOptions: unsel }
  }, [options])

  return (
    <Command loop>
      <CommandInput autoFocus placeholder={t('search', locale)} />
      <CommandEmpty>{t('noresults', locale)}</CommandEmpty>
      <CommandList>
        <CommandGroup className={cn(selectedOptions.length === 0 && 'hidden')}>
          {selectedOptions.map((option) => (
            <OptionItem key={option.value} option={option} onToggle={handleToggle} />
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup className={cn(unselectedOptions.length === 0 && 'hidden')}>
          {unselectedOptions.map((option) => (
            <OptionItem key={option.value} option={option} onToggle={handleToggle} />
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

function FilterValueRemoteMultiOptionController<TData>({
  filter,
  column,
  actions,
  remoteConfig,
  locale = 'en',
}: FilterValueControllerProps<TData, 'multiOption'> & { remoteConfig: NonNullable<unknown> }) {
  // biome-ignore lint/suspicious/noExplicitAny: Remote config needs flexible typing
  const config = remoteConfig as any

  const {
    options: remoteOptions,
    setSearchTerm,
    isLoading,
    loadMore,
    hasNextPage,
    isFetchingNextPage,
  } = useRemoteSelect({
    queryKey: config.queryKey,
    fetchService: config.fetchService,
    optionExtractor: config.optionExtractor,
    searchKey: config.searchKey,
    pageSize: config.pageSize,
    enableOffsetMode: config.enableOffsetMode ?? false,
    selectedValue:
      config.fetchResource && config.optionFromResource
        ? filter?.values?.[0] || config.selectedValue
        : undefined,
    fetchResource: config.fetchResource,
    optionFromResource: config.optionFromResource,
  })

  const staticOptions = useMemo(() => {
    try {
      return column.getOptions()
    } catch {
      return []
    }
  }, [column])

  const allOptions: ColumnOption[] = useMemo(() => {
    const remoteValues = new Set(remoteOptions.map((o) => o.value))
    const uniqueStatic = staticOptions.filter((o) => !remoteValues.has(o.value))
    const normalizedRemote: ColumnOption[] = remoteOptions.map((o) => ({
      label: o.label,
      value: o.value,
      icon: o.icon as ColumnOption['icon'],
    }))
    return [...uniqueStatic, ...normalizedRemote]
  }, [staticOptions, remoteOptions])

  const options: Array<ColumnOptionExtended & { initialSelected: boolean }> = useMemo(
    () =>
      allOptions.map((o) => ({
        ...o,
        selected: filter?.values.includes(o.value) ?? false,
        initialSelected: filter?.values.includes(o.value) ?? false,
        count: 0,
      })),
    [allOptions, filter?.values],
  )

  const handleToggle = useCallback(
    (value: string, checked: boolean) => {
      if (checked) actions.addFilterValue(column, [value])
      else actions.removeFilterValue(column, [value])
    },
    [actions, column],
  )

  const { selectedOptions, unselectedOptions } = useMemo(() => {
    const sel: typeof options = []
    const unsel: typeof options = []
    for (const o of options) {
      if (o.selected) sel.push(o)
      else unsel.push(o)
    }
    return { selectedOptions: sel, unselectedOptions: unsel }
  }, [options])

  const listRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    const el = listRef.current
    if (!el || !hasNextPage || isFetchingNextPage) return
    const { scrollTop, scrollHeight, clientHeight } = el
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      loadMore()
    }
  }, [hasNextPage, isFetchingNextPage, loadMore])

  const useClientSideSearch = config.clientSideSearch ?? false

  return (
    <Command loop shouldFilter={useClientSideSearch}>
      <CommandInput
        autoFocus
        placeholder={t('search', locale)}
        onValueChange={useClientSideSearch ? undefined : setSearchTerm}
      />
      {!isLoading && allOptions.length === 0 && (
        <CommandEmpty>{t('noresults', locale)}</CommandEmpty>
      )}
      <CommandList ref={listRef} onScroll={handleScroll}>
        {isLoading && allOptions.length === 0 ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <CommandGroup className={cn(selectedOptions.length === 0 && 'hidden')}>
              {selectedOptions.map((option) => (
                <OptionItem key={option.value} option={option} onToggle={handleToggle} />
              ))}
            </CommandGroup>
            {selectedOptions.length > 0 && unselectedOptions.length > 0 && <CommandSeparator />}
            <CommandGroup className={cn(unselectedOptions.length === 0 && 'hidden')}>
              {unselectedOptions.map((option) => (
                <OptionItem key={option.value} option={option} onToggle={handleToggle} />
              ))}
            </CommandGroup>
            {isFetchingNextPage && (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="size-3 animate-spin text-muted-foreground" />
              </div>
            )}
          </>
        )}
      </CommandList>
    </Command>
  )
}

type DatePreset = {
  label: string
  value: string
  getDateRange: () => DateRange
}

// Standard presets stay within 30 days: they are safe for every data source,
// including retention-limited APIs (call logs serve only the last 30 days).
const STANDARD_DATE_PRESETS: DatePreset[] = [
  {
    label: 'Today',
    value: 'today',
    getDateRange: () => ({ from: startOfDay(new Date()), to: new Date() }),
  },
  {
    label: 'Last 24 hours',
    value: 'last-24h',
    getDateRange: () => ({ from: subHours(new Date(), 24), to: new Date() }),
  },
  {
    label: 'Last 7 days',
    value: 'last-7d',
    getDateRange: () => ({ from: subDays(new Date(), 7), to: new Date() }),
  },
  {
    label: 'Last 30 days',
    value: 'last-30d',
    getDateRange: () => ({ from: subDays(new Date(), 30), to: new Date() }),
  },
]

// Extended presets exceed 30 days; opt-in via dateRangeOptions.extendedPresets
// (the analytics reports, where ClickHouse serves full history).
const EXTENDED_DATE_PRESETS: DatePreset[] = [
  ...STANDARD_DATE_PRESETS,
  {
    label: 'This month',
    value: 'mtd',
    getDateRange: () => ({ from: startOfMonth(new Date()), to: new Date() }),
  },
  {
    label: 'Last month',
    value: 'last-month',
    getDateRange: () => ({
      from: startOfMonth(subMonths(new Date(), 1)),
      to: endOfMonth(subMonths(new Date(), 1)),
    }),
  },
  {
    label: 'Last 90 days',
    value: 'last-90d',
    getDateRange: () => ({ from: subDays(new Date(), 90), to: new Date() }),
  },
  {
    label: 'This year',
    value: 'ytd',
    getDateRange: () => ({ from: startOfYear(new Date()), to: new Date() }),
  },
]

export function FilterValueDateController<TData>({
  filter,
  column,
  actions,
  onClose,
}: FilterValueControllerProps<TData, 'date'>) {
  const [date, setDate] = useState<DateRange | undefined>(
    filter?.values[0]
      ? {
          from: toLocalDate(filter.values[0]),
          to: filter.values[1] ? toLocalDate(filter.values[1]) : undefined,
        }
      : undefined,
  )
  const [tempDate, setTempDate] = useState<DateRange | undefined>(date)
  const [selectedPreset, setSelectedPreset] = useState<string>('')
  const [showCustom, setShowCustom] = useState(false)

  const handlePresetSelect = (preset: DatePreset) => {
    const dateRange = preset.getDateRange()
    setDate(dateRange)
    setSelectedPreset(preset.value)

    const newValues = dateRange.from && dateRange.to ? [dateRange.from, dateRange.to] : []
    actions.setFilterValue(column, newValues)
  }

  const handleTempDateChange = (value: DateRange | undefined) => {
    setTempDate(value)
  }

  const handleCancel = () => {
    setTempDate(date)
    setShowCustom(false)
  }

  const handleApply = () => {
    const start = tempDate?.from
    const end =
      start && tempDate && tempDate.to && !isEqual(start, tempDate.to) ? tempDate.to : undefined

    const newDate = { from: start, to: end }
    setDate(newDate)
    setSelectedPreset('custom')

    const isRange = start && end
    const newValues = isRange ? [start, end] : start ? [start] : []
    actions.setFilterValue(column, newValues)
    setShowCustom(false)
    onClose?.()
  }

  if (showCustom) {
    const disabledProp = column.dateRangeOptions?.disableFuture ? { after: new Date() } : undefined

    return (
      <Command className="w-auto">
        <CommandList className="max-h-fit">
          <CommandGroup>
            <div>
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={tempDate?.from ?? subMonths(new Date(), 1)}
                selected={tempDate}
                onSelect={handleTempDateChange}
                numberOfMonths={2}
                disabled={disabledProp}
                fromDate={column.dateRangeOptions?.minDate}
                toDate={column.dateRangeOptions?.maxDate}
              />
              <div className="flex items-center justify-end p-3 border-t border-st2 gap-2">
                <Button variant="outline" size="xs" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button size="xs" onClick={handleApply}>
                  Apply
                </Button>
              </div>
            </div>
          </CommandGroup>
        </CommandList>
      </Command>
    )
  }

  return (
    <Command className="w-[180px]">
      <CommandList className="max-h-fit">
        <CommandGroup>
          {(column.dateRangeOptions?.extendedPresets
            ? EXTENDED_DATE_PRESETS
            : STANDARD_DATE_PRESETS
          ).map((preset) => (
            <CommandItem
              key={preset.value}
              onSelect={() => handlePresetSelect(preset)}
              className="flex items-center justify-between"
              data-testid={`option-date-preset-${preset.value}`}
            >
              {preset.label}
              {selectedPreset === preset.value && <Check className="h-4 w-4" />}
            </CommandItem>
          ))}
          <CommandItem
            onSelect={() => setShowCustom(true)}
            className="flex items-center justify-between"
            data-testid="option-date-preset-custom"
          >
            Custom
            {selectedPreset === 'custom' && <Check className="h-4 w-4" />}
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

export function FilterValueTextController<TData>({
  filter,
  column,
  actions,
  locale = 'en',
}: FilterValueControllerProps<TData, 'text'>) {
  const changeText = (value: string | number) => {
    actions.setFilterValue(column, [String(value)])
  }

  return (
    <Command>
      <CommandList className="max-h-fit">
        <CommandGroup>
          <CommandItem>
            <DebouncedInput
              // Room to edit long values like UUIDs (popover caps at max-w-96).
              className="w-80"
              placeholder={column.placeholder ?? t('search', locale)}
              autoFocus
              value={filter?.values[0] ?? ''}
              onChange={changeText}
            />
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

export function FilterValueNumberController<TData>({
  filter,
  column,
  actions,
  locale = 'en',
}: FilterValueControllerProps<TData, 'number'>) {
  const minMax = useMemo(() => column.getFacetedMinMaxValues(), [column])

  // Local state for values
  const [values, setValues] = useState(filter?.values ?? [0, 0])

  // Sync with parent filter changes
  useEffect(() => {
    if (
      filter?.values &&
      (filter.values.length !== values.length || !filter.values.every((v, i) => v === values[i]))
    ) {
      setValues(filter.values)
    }
  }, [filter?.values])

  const isNumberRange =
    // filter && values.length === 2
    filter && numberFilterOperators[filter.operator].target === 'multiple'

  // Create debounced functions using lodash with cleanup
  const setFilterOperatorDebouncedRef = useRef(debounce(actions.setFilterOperator, 500))
  const setFilterValueDebouncedRef = useRef(debounce(actions.setFilterValue, 500))

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setFilterOperatorDebouncedRef.current.cancel()
      setFilterValueDebouncedRef.current.cancel()
    }
  }, [])

  const changeNumber = (value: number[]) => {
    setValues(value)
    // biome-ignore lint/suspicious/noExplicitAny: Type variance issue with debounced function
    setFilterValueDebouncedRef.current(column as any, value)
  }

  const changeMinNumber = (value: number) => {
    const newValues = createNumberRange([value, values[1]])
    setValues(newValues)
    // biome-ignore lint/suspicious/noExplicitAny: Type variance issue with debounced function
    setFilterValueDebouncedRef.current(column as any, newValues)
  }

  const changeMaxNumber = (value: number) => {
    const newValues = createNumberRange([values[0], value])
    setValues(newValues)
    // biome-ignore lint/suspicious/noExplicitAny: Type variance issue with debounced function
    setFilterValueDebouncedRef.current(column as any, newValues)
  }

  const changeType = useCallback(
    (type: 'single' | 'range') => {
      let newValues: number[] = []
      if (type === 'single')
        newValues = [values[0]] // Keep the first value for single mode
      else if (!minMax) newValues = createNumberRange([values[0], values[1] ?? 0])
      else {
        const value = values[0]
        newValues =
          value - minMax[0] < minMax[1] - value
            ? createNumberRange([value, minMax[1]])
            : createNumberRange([minMax[0], value])
      }

      const newOperator = type === 'single' ? 'is' : 'is between'

      // Update local state
      setValues(newValues)

      // Cancel in-flight debounced calls to prevent flicker/race conditions
      setFilterOperatorDebouncedRef.current.cancel()
      setFilterValueDebouncedRef.current.cancel()

      // Update global filter state atomically
      actions.setFilterOperator(column.id, newOperator)
      actions.setFilterValue(column, newValues)
    },
    [values, column, actions, minMax],
  )

  return (
    <Command>
      <CommandList className="w-[300px] px-2 py-2">
        <CommandGroup>
          <div className="flex flex-col w-full">
            <Tabs
              value={isNumberRange ? 'range' : 'single'}
              onValueChange={(v) => changeType(v as 'single' | 'range')}
            >
              <TabsList className="w-full">
                <TabsTrigger
                  value="single"
                  className="font-inter text-s-500 normal-case tracking-normal"
                >
                  {t('single', locale)}
                </TabsTrigger>
                <TabsTrigger
                  value="range"
                  className="font-inter text-s-500 normal-case tracking-normal"
                >
                  {t('range', locale)}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="single" className="flex flex-col gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <span className="font-inter text-s-500 normal-case tracking-normal">
                    {t('value', locale)}
                  </span>
                  <DebouncedInput
                    id="single"
                    type="number"
                    className="font-jetbrains text-xs-400 tabular-nums"
                    value={values[0].toString()} // Use values[0] directly
                    onChange={(v) => changeNumber([Number(v)])}
                  />
                </div>
              </TabsContent>
              <TabsContent value="range" className="flex flex-col gap-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <span className="font-inter text-s-500 normal-case tracking-normal">
                      {t('min', locale)}
                    </span>
                    <DebouncedInput
                      type="number"
                      className="font-jetbrains text-xs-400 tabular-nums"
                      value={values[0]}
                      onChange={(v) => changeMinNumber(Number(v))}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-inter text-s-500 normal-case tracking-normal">
                      {t('max', locale)}
                    </span>
                    <DebouncedInput
                      type="number"
                      className="font-jetbrains text-xs-400 tabular-nums"
                      value={values[1]}
                      onChange={(v) => changeMaxNumber(Number(v))}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

export function FilterValueDurationRangeController<TData>({
  filter,
  column,
  actions,
}: FilterValueControllerProps<TData, 'durationRange'>) {
  const fromOptions = column.durationRangeOptions?.fromOptions ?? []
  const toOptions = column.durationRangeOptions?.toOptions ?? []

  const defaultFrom = fromOptions[0]?.value ?? ''
  const defaultTo = toOptions[0]?.value ?? ''

  const [fromValue, setFromValue] = useState<string>(
    filter?.values[0] != null ? String(filter.values[0]) : defaultFrom,
  )
  const [toValue, setToValue] = useState<string>(
    filter?.values[1] != null ? String(filter.values[1]) : defaultTo,
  )

  const handleFromChange = useCallback(
    (value: string) => {
      setFromValue(value)
      if (value && toValue) {
        actions.setFilterValue(column, [Number(value), Number(toValue)])
      }
    },
    [toValue, actions, column],
  )

  const handleToChange = useCallback(
    (value: string) => {
      setToValue(value)
      if (fromValue && value) {
        actions.setFilterValue(column, [Number(fromValue), Number(value)])
      }
    },
    [fromValue, actions, column],
  )

  return (
    <div className="w-[280px] px-3 py-3">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <span className="font-inter text-s-500 normal-case tracking-normal w-10 shrink-0">
            From
          </span>
          <Select value={fromValue} onValueChange={handleFromChange}>
            <SelectTrigger
              data-testid="duration-range-from-select"
              className="h-8 font-inter text-s-400 normal-case tracking-normal"
            >
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {fromOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-inter text-s-500 normal-case tracking-normal w-10 shrink-0">
            To
          </span>
          <Select value={toValue} onValueChange={handleToChange}>
            <SelectTrigger
              data-testid="duration-range-to-select"
              className="h-8 font-inter text-s-400 normal-case tracking-normal"
            >
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {toOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  )
}
